import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  QrCodeScanner as BarcodeIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CartItem {
  id: string;
  itemType: 'Amenity' | 'Custom' | 'Service' | 'Fee';
  amenityId?: string;
  name: string;
  description?: string;
  category: string;
  unitPrice: number;
  quantity: number;
  discount?: {
    type: 'Percentage' | 'Fixed Amount';
    value: number;
    reason: string;
  };
  taxRate?: number;
  lineTotal: number;
}

interface Customer {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  type: 'guest' | 'walkIn';
  reservations?: any[];
}

interface Product {
  _id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  unit: string;
  inventory: {
    isTracked: boolean;
    currentStock: number;
  };
  tags: string[];
}

const POSPage: React.FC = () => {
  const { user } = useAuth();
  const [currentShift, setCurrentShift] = useState<any>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  
  // Search states
  const [productSearch, setProductSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // Dialog states
  const [customerDialog, setCustomerDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [shiftDialog, setShiftDialog] = useState(false);
  const [barcodeDialog, setBarcodeDialog] = useState(false);
  
  // Barcode scanning states
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  
  // Form states
  const [customItem, setCustomItem] = useState({
    name: '',
    description: '',
    category: 'Misc',
    unitPrice: 0,
    quantity: 1
  });
  
  const [walkInCustomer, setWalkInCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  const [payment, setPayment] = useState({
    method: 'Cash',
    amount: 0,
    reference: '',
    tendered: 0,
    giftCard: {
      cardNumber: '',
      pin: '',
      validated: false,
      availableBalance: 0
    }
  });
  
  const [shiftData, setShiftData] = useState({
    location: 'Front Desk',
    registerId: 'REG001',
    registerName: 'Main Register',
    openingCash: {
      amount: 100,
      breakdown: {
        hundreds: 0, fifties: 0, twenties: 2, tens: 3,
        fives: 4, ones: 10, quarters: 0, dimes: 0, nickels: 0, pennies: 0
      }
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    checkCurrentShift();
    searchProducts('');
  }, []);

  const checkCurrentShift = async () => {
    try {
      const response = await api.get('/pos/shifts/current');
      setCurrentShift(response.data);
    } catch (err: any) {
      // No current shift is fine
      console.log('No current shift found');
    }
  };

  const startShift = async () => {
    try {
      setLoading(true);
      const response = await api.post('/pos/shifts/start', shiftData);
      setCurrentShift(response.data.shift);
      setShiftDialog(false);
      setSuccessMessage('Shift started successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start shift');
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (query: string) => {
    try {
      const response = await api.get('/pos/products/search', {
        params: { 
          query,
          category: selectedCategory !== 'All' ? selectedCategory : undefined
        }
      });
      setProducts(response.data);
    } catch (err: any) {
      console.error('Failed to search products:', err);
    }
  };

  const searchCustomers = async (query: string) => {
    if (query.length < 2) {
      setCustomers([]);
      return;
    }
    
    try {
      const response = await api.get('/pos/customers/search', { params: { query } });
      setCustomers(response.data);
    } catch (err: any) {
      console.error('Failed to search customers:', err);
    }
  };

  const lookupBarcode = async (barcode: string) => {
    if (!barcode.trim()) {
      setError('Please enter a barcode');
      return;
    }

    try {
      setBarcodeLoading(true);
      const response = await api.get(`/amenities/barcode/${barcode.trim()}`);
      const product = response.data;
      
      // Convert the response to our Product interface format
      const formattedProduct: Product = {
        _id: product.id,
        name: product.name,
        description: product.description,
        category: product.category,
        price: product.price,
        unit: product.unit,
        inventory: product.inventory,
        tags: []
      };
      
      addToCart(formattedProduct);
      setBarcodeInput('');
      setBarcodeDialog(false);
      setSuccessMessage(`Added ${product.name} to cart`);
      
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(`No product found with barcode: ${barcode}`);
      } else if (err.response?.status === 400) {
        setError(err.response.data.error || 'Product unavailable');
      } else {
        setError('Failed to lookup barcode');
      }
    } finally {
      setBarcodeLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera if available
          width: { ideal: 640 },
          height: { ideal: 480 }
        } 
      });
      setCameraStream(stream);
    } catch (err) {
      setError('Camera access denied. Please use manual barcode entry.');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const handleBarcodeDialogClose = () => {
    setBarcodeDialog(false);
    setBarcodeInput('');
    stopCamera();
  };

  const validateGiftCard = async () => {
    const { cardNumber, pin } = payment.giftCard;
    
    if (!cardNumber.trim() || !pin.trim()) {
      setError('Please enter both gift card number and PIN');
      return;
    }

    try {
      setBarcodeLoading(true);
      const response = await api.post('/gift-cards/validate', {
        cardNumber: cardNumber.trim(),
        pin: pin.trim(),
        amount: payment.amount
      });

      setPayment(prev => ({
        ...prev,
        giftCard: {
          ...prev.giftCard,
          validated: true,
          availableBalance: response.data.balance
        }
      }));

      setSuccessMessage(`Gift card validated. Available balance: $${response.data.balance.toFixed(2)}`);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate gift card');
      setPayment(prev => ({
        ...prev,
        giftCard: {
          ...prev.giftCard,
          validated: false,
          availableBalance: 0
        }
      }));
    } finally {
      setBarcodeLoading(false);
    }
  };

  const addToCart = (product: Product, quantity: number = 1) => {
    const existingItem = cart.find(item => item.amenityId === product._id);
    
    if (existingItem) {
      updateCartItem(existingItem.id, { quantity: existingItem.quantity + quantity });
    } else {
      const newItem: CartItem = {
        id: Date.now().toString(),
        itemType: 'Amenity',
        amenityId: product._id,
        name: product.name,
        description: product.description,
        category: product.category,
        unitPrice: product.price,
        quantity,
        lineTotal: product.price * quantity
      };
      setCart(prev => [...prev, newItem]);
    }
  };

  const addCustomItem = () => {
    const newItem: CartItem = {
      id: Date.now().toString(),
      itemType: 'Custom',
      name: customItem.name,
      description: customItem.description,
      category: customItem.category,
      unitPrice: customItem.unitPrice,
      quantity: customItem.quantity,
      lineTotal: customItem.unitPrice * customItem.quantity
    };
    
    setCart(prev => [...prev, newItem]);
    setCustomItem({
      name: '',
      description: '',
      category: 'Misc',
      unitPrice: 0,
      quantity: 1
    });
  };

  const updateCartItem = (itemId: string, updates: Partial<CartItem>) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, ...updates };
        updated.lineTotal = updated.unitPrice * updated.quantity;
        return updated;
      }
      return item;
    }));
  };

  const removeFromCart = (itemId: string) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomer(null);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.lineTotal, 0);
  };

  const processTransaction = async () => {
    if (!currentShift) {
      setError('Please start your shift first');
      return;
    }
    
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create transaction
      const transactionData = {
        customer: {
          guestId: customer?.type === 'guest' ? customer._id : null,
          walkIn: customer?.type === 'walkIn' ? walkInCustomer : null
        },
        items: cart.map(item => ({
          itemType: item.itemType,
          amenityId: item.amenityId,
          name: item.name,
          description: item.description,
          category: item.category,
          unitPrice: item.unitPrice,
          quantity: item.quantity,
          discount: item.discount,
          taxRate: item.taxRate || 0
        })),
        location: currentShift.location
      };
      
      const transactionResponse = await api.post('/pos/transactions', transactionData);
      const transaction = transactionResponse.data.transaction;
      
      // Process payment
      const paymentData = {
        paymentMethod: payment.method,
        amount: payment.amount,
        reference: payment.reference,
        tendered: payment.method === 'Cash' ? payment.tendered : payment.amount
      };
      
      const paymentResponse = await api.post(`/pos/transactions/${transaction._id}/payment`, paymentData);
      
      setSuccessMessage(`Transaction completed successfully! Transaction #${transaction.transactionNumber}`);
      setPaymentDialog(false);
      clearCart();
      
      // Reset payment form
      setPayment({
        method: 'Cash',
        amount: 0,
        reference: '',
        tendered: 0,
        giftCard: {
          cardNumber: '',
          pin: '',
          validated: false,
          availableBalance: 0
        }
      });
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process transaction');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', 'Recreation', 'Food & Beverage', 'Equipment Rental', 'Store Items', 'Services', 'Firewood & Ice', 'Pet Services', 'Laundry'];

  if (!currentShift) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Point of Sale System
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You need to start your cashier shift before using the POS system.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setShiftDialog(true)}
          >
            Start Shift
          </Button>
        </Paper>

        {/* Start Shift Dialog */}
        <Dialog open={shiftDialog} onClose={() => setShiftDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Start Cashier Shift</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={shiftData.location}
                    onChange={(e) => setShiftData(prev => ({ ...prev, location: e.target.value }))}
                    label="Location"
                  >
                    <MenuItem value="Front Desk">Front Desk</MenuItem>
                    <MenuItem value="Camp Store">Camp Store</MenuItem>
                    <MenuItem value="Pool Area">Pool Area</MenuItem>
                    <MenuItem value="Activity Center">Activity Center</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Register ID"
                  value={shiftData.registerId}
                  onChange={(e) => setShiftData(prev => ({ ...prev, registerId: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opening Cash Amount"
                  type="number"
                  value={shiftData.openingCash.amount}
                  onChange={(e) => setShiftData(prev => ({ 
                    ...prev, 
                    openingCash: { ...prev.openingCash, amount: parseFloat(e.target.value) || 0 }
                  }))}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShiftDialog(false)}>Cancel</Button>
            <Button onClick={startShift} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Start Shift'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Product Search & Categories */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <TextField
                size="small"
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  searchProducts(e.target.value);
                }}
                InputProps={{
                  startAdornment: <SearchIcon color="action" />
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<BarcodeIcon />}
                onClick={() => setBarcodeDialog(true)}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Scan
              </Button>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    searchProducts(productSearch);
                  }}
                  label="Category"
                >
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Products Grid */}
            <Grid container spacing={1}>
              {products.map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product._id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: 'action.hover' },
                      height: '100%'
                    }}
                    onClick={() => addToCart(product)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {product.category}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ fontSize: '0.9rem', mt: 0.5 }}>
                        ${product.price.toFixed(2)}
                      </Typography>
                      {product.inventory.isTracked && (
                        <Chip 
                          label={`Stock: ${product.inventory.currentStock}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 20, mt: 0.5 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Gift Card Sales */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="success.contrastText">
                Gift Card Sales
              </Typography>
              <Grid container spacing={1}>
                {[25, 50, 100, 200].map((amount) => (
                  <Grid item xs={3} key={amount}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      onClick={() => {
                        const giftCardItem: CartItem = {
                          id: `gift-card-${Date.now()}`,
                          itemType: 'Custom',
                          name: `$${amount} Gift Card`,
                          description: 'Digital gift card',
                          category: 'Gift Cards',
                          unitPrice: amount,
                          quantity: 1,
                          lineTotal: amount
                        };
                        setCart(prev => [...prev, giftCardItem]);
                      }}
                      sx={{ 
                        backgroundColor: 'success.main',
                        '&:hover': { backgroundColor: 'success.dark' }
                      }}
                    >
                      ${amount}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Custom Item Form */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Add Custom Item</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Item Name"
                    value={customItem.name}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Price"
                    type="number"
                    value={customItem.unitPrice}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Qty"
                    type="number"
                    value={customItem.quantity}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addCustomItem}
                    disabled={!customItem.name || customItem.unitPrice <= 0}
                    sx={{ height: '40px', minWidth: 'unset', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Cart & Customer */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 'fit-content' }}>
            {/* Current Shift Info */}
            <Box sx={{ mb: 2, p: 1, backgroundColor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
              <Typography variant="subtitle2">
                Shift: {currentShift.shiftNumber} | {currentShift.location}
              </Typography>
              <Typography variant="body2">
                Cashier: {currentShift.cashier.firstName} {currentShift.cashier.lastName}
              </Typography>
            </Box>

            {/* Customer Selection */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon color="action" />
                <Typography variant="subtitle2">Customer</Typography>
                {customer && (
                  <IconButton size="small" onClick={() => setCustomer(null)}>
                    <ClearIcon />
                  </IconButton>
                )}
              </Box>
              
              {customer ? (
                <Box sx={{ p: 1, backgroundColor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2">
                    {customer.firstName} {customer.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {customer.email}
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => setCustomerDialog(true)}
                >
                  Select Customer
                </Button>
              )}
            </Box>

            {/* Cart */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <CartIcon color="action" />
                <Typography variant="subtitle2">
                  Cart ({cart.length} items)
                </Typography>
              </Box>
              {cart.length > 0 && (
                <Button size="small" onClick={clearCart} startIcon={<ClearIcon />}>
                  Clear
                </Button>
              )}
            </Box>

            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
              {cart.map((item) => (
                <ListItem key={item.id} divider>
                  <ListItemText
                    primary={item.name}
                    secondary={`$${item.unitPrice.toFixed(2)} × ${item.quantity}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <IconButton 
                        size="small" 
                        onClick={() => updateCartItem(item.id, { quantity: Math.max(1, item.quantity - 1) })}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
                        {item.quantity}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => updateCartItem(item.id, { quantity: item.quantity + 1 })}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => removeFromCart(item.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {cart.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <CartIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">
                  Cart is empty
                </Typography>
              </Box>
            )}

            {/* Total & Checkout */}
            {cart.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" color="primary">
                    ${getCartTotal().toFixed(2)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<PaymentIcon />}
                  onClick={() => {
                    setPayment(prev => ({ ...prev, amount: getCartTotal() }));
                    setPaymentDialog(true);
                  }}
                >
                  Checkout
                </Button>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Customer Selection Dialog */}
      <Dialog open={customerDialog} onClose={() => setCustomerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Customer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search by name, email, or phone..."
            value={customerSearch}
            onChange={(e) => {
              setCustomerSearch(e.target.value);
              searchCustomers(e.target.value);
            }}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon color="action" />
            }}
          />
          
          <List>
            {customers.map((cust) => (
              <ListItem 
                key={cust._id} 
                button 
                onClick={() => {
                  setCustomer(cust);
                  setCustomerDialog(false);
                }}
              >
                <ListItemText
                  primary={`${cust.firstName} ${cust.lastName}`}
                  secondary={cust.email}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>Walk-in Customer</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Name"
                value={walkInCustomer.name}
                onChange={(e) => setWalkInCustomer(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Email"
                value={walkInCustomer.email}
                onChange={(e) => setWalkInCustomer(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  if (walkInCustomer.name) {
                    setCustomer({
                      _id: 'walk-in',
                      firstName: walkInCustomer.name.split(' ')[0] || '',
                      lastName: walkInCustomer.name.split(' ').slice(1).join(' ') || '',
                      email: walkInCustomer.email,
                      type: 'walkIn'
                    });
                    setCustomerDialog(false);
                  }
                }}
                disabled={!walkInCustomer.name}
              >
                Add Walk-in Customer
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Payment</DialogTitle>
        <DialogContent>
          <Typography variant="h5" color="primary" gutterBottom>
            Total: ${getCartTotal().toFixed(2)}
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={payment.method}
                  onChange={(e) => setPayment(prev => ({ ...prev, method: e.target.value }))}
                  label="Payment Method"
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Credit Card">Credit Card</MenuItem>
                  <MenuItem value="Debit Card">Debit Card</MenuItem>
                  <MenuItem value="Check">Check</MenuItem>
                  <MenuItem value="Gift Card">Gift Card</MenuItem>
                  <MenuItem value="Charge to Room">Charge to Room</MenuItem>
                  <MenuItem value="Comp">Comp</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={payment.amount}
                onChange={(e) => setPayment(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            
            {payment.method === 'Cash' && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cash Tendered"
                  type="number"
                  value={payment.tendered}
                  onChange={(e) => setPayment(prev => ({ ...prev, tendered: parseFloat(e.target.value) || 0 }))}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
            )}
            
            {(['Credit Card', 'Debit Card', 'Check'].includes(payment.method)) && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Reference Number"
                  value={payment.reference}
                  onChange={(e) => setPayment(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Last 4 digits, check number, etc."
                />
              </Grid>
            )}
            
            {payment.method === 'Gift Card' && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Gift Card Number"
                    value={payment.giftCard.cardNumber}
                    onChange={(e) => setPayment(prev => ({ 
                      ...prev, 
                      giftCard: { ...prev.giftCard, cardNumber: e.target.value, validated: false }
                    }))}
                    placeholder="Enter gift card number"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="PIN"
                    type="password"
                    value={payment.giftCard.pin}
                    onChange={(e) => setPayment(prev => ({ 
                      ...prev, 
                      giftCard: { ...prev.giftCard, pin: e.target.value, validated: false }
                    }))}
                    placeholder="Enter PIN"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" gap={1} alignItems="center">
                    <Button
                      variant="outlined"
                      onClick={validateGiftCard}
                      disabled={barcodeLoading || !payment.giftCard.cardNumber || !payment.giftCard.pin}
                      startIcon={barcodeLoading ? <CircularProgress size={20} /> : undefined}
                    >
                      {barcodeLoading ? 'Validating...' : 'Validate Gift Card'}
                    </Button>
                    {payment.giftCard.validated && (
                      <Chip
                        label={`Available: $${payment.giftCard.availableBalance.toFixed(2)}`}
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
          
          {payment.method === 'Cash' && payment.tendered > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Change Due: ${Math.max(0, payment.tendered - payment.amount).toFixed(2)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Cancel</Button>
          <Button 
            onClick={processTransaction}
            variant="contained"
            disabled={
              loading || 
              payment.amount !== getCartTotal() ||
              (payment.method === 'Gift Card' && !payment.giftCard.validated)
            }
            startIcon={loading ? <CircularProgress size={20} /> : <ReceiptIcon />}
          >
            {loading ? 'Processing...' : 'Complete Sale'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Barcode Scanner Dialog */}
      <Dialog open={barcodeDialog} onClose={handleBarcodeDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BarcodeIcon />
            Barcode Scanner
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter barcode manually or use camera to scan
          </Typography>
          
          {/* Manual Barcode Entry */}
          <TextField
            fullWidth
            label="Barcode"
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                lookupBarcode(barcodeInput);
              }
            }}
            InputProps={{
              startAdornment: <BarcodeIcon color="action" />
            }}
            sx={{ mb: 2 }}
            autoFocus
          />
          
          <Box display="flex" gap={2} mb={2}>
            <Button
              variant="contained"
              onClick={() => lookupBarcode(barcodeInput)}
              disabled={!barcodeInput.trim() || barcodeLoading}
              startIcon={barcodeLoading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ flex: 1 }}
            >
              {barcodeLoading ? 'Looking up...' : 'Lookup Barcode'}
            </Button>
            <Button
              variant="outlined"
              onClick={cameraStream ? stopCamera : startCamera}
              startIcon={<CameraIcon />}
              sx={{ flex: 1 }}
            >
              {cameraStream ? 'Stop Camera' : 'Start Camera'}
            </Button>
          </Box>

          {/* Camera Preview */}
          {cameraStream && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Camera Preview
              </Typography>
              <video
                ref={(video) => {
                  if (video && cameraStream) {
                    video.srcObject = cameraStream;
                    video.play();
                  }
                }}
                style={{
                  width: '100%',
                  maxHeight: '300px',
                  backgroundColor: '#000',
                  borderRadius: '4px'
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Position barcode in front of camera. You may need to manually enter the barcode if scanning doesn't work automatically.
              </Typography>
            </Box>
          )}

          {/* Quick Access Common Barcodes */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Test Barcodes (Demo)
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {['123456789012', '987654321098', '456789123456'].map((testBarcode) => (
                <Chip
                  key={testBarcode}
                  label={testBarcode}
                  size="small"
                  onClick={() => setBarcodeInput(testBarcode)}
                  clickable
                  variant="outlined"
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Note: These are demo barcodes. Real products need actual barcodes in the system.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBarcodeDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default POSPage;